import { isEmpty, sanitizeValues } from '@intelligence/v3/utils/common';
import { resolveApiField } from './fieldMapUtils';

import type { IFilterItemConfig } from '@intelligence/v3/filterCore/types';

const EMPTY_ARRAY: string[] = [];
const FINITE_NUMBER_TEXT_PATTERN = /^-?(?:(?:\d+\.?\d*)|(?:\.\d+))$/;

export const getStringArrayValue = (value: unknown): string[] => {
  return Array.isArray(value) ? value : EMPTY_ARRAY;
};

const getOptionChildren = (option: any): any[] | undefined => {
  return Array.isArray(option?.children) ? option.children : option?.options;
};

const collectOptionValues = (options?: any[], leafOnly = false): string[] => {
  if (!Array.isArray(options)) {
    return [];
  }

  return options.reduce<string[]>((result, option) => {
    const children = getOptionChildren(option);

    if (Array.isArray(children) && children.length > 0) {
      if (!leafOnly && typeof option?.value === 'string' && option.value) {
        result.push(option.value);
      }

      collectOptionValues(children, leafOnly).forEach((childValue) => {
        result.push(childValue);
      });

      return result;
    }

    const value = option?.value;

    if (typeof value === 'string' && value) {
      result.push(value);
    }

    return result;
  }, []);
};

const collectLeafOptionValues = (options?: any[]): string[] => {
  return collectOptionValues(options, true);
};

const getDefaultOptionValuesByItem = (item?: any): string[] => {
  if (item?.type === 'platform') {
    return ['appstore', 'googleplay'];
  }

  if (item?.type === 'data_source') {
    if (item?.controlProps?.showDatabrain) {
      return ['ogdb', 'appannie', 'sensortower'];
    }

    return ['appannie', 'sensortower'];
  }

  return [];
};

const getOptionValuesByItem = (item?: any): string[] => {
  const optionValues = collectOptionValues(item?.controlProps?.options);
  const resolvedOptionValues = isEmpty(optionValues) ? getDefaultOptionValuesByItem(item) : optionValues;

  return Array.from(new Set(resolvedOptionValues));
};

const getLeafOptionValuesByItem = (item?: any): string[] => {
  const optionValues = collectLeafOptionValues(item?.controlProps?.options);
  const resolvedOptionValues = isEmpty(optionValues) ? getDefaultOptionValuesByItem(item) : optionValues;

  return Array.from(new Set(resolvedOptionValues));
};

const normalizeStringArrayByOptionValues = (
  value: unknown,
  optionValues?: string[],
  emptyFallback: 'all' | 'empty' = 'all',
): string[] => {
  const values = sanitizeValues(getStringArrayValue(value));
  const normalizedOptionValues = sanitizeValues(optionValues || []);

  if (isEmpty(normalizedOptionValues)) {
    return values;
  }

  if (isEmpty(values)) {
    return emptyFallback === 'all' ? normalizedOptionValues : [];
  }

  const optionValueSet = new Set(normalizedOptionValues);
  const validValues = values.filter((itemValue) => optionValueSet.has(itemValue));

  if (isEmpty(validValues)) {
    return emptyFallback === 'all' ? normalizedOptionValues : [];
  }

  return validValues;
};

export const normalizeStringArrayByOptions = (value: unknown, options?: any[]): string[] => {
  return normalizeStringArrayByOptionValues(value, collectOptionValues(options));
};

export const normalizeStringArrayByItemOptions = (item: any, value: unknown): string[] => {
  return normalizeStringArrayByOptionValues(value, getOptionValuesByItem(item));
};

/**
 * 保留空值语义：
 * - 空值 / 全非法值 -> []
 * - 部分合法值 -> 仅保留合法值
 *
 * 适用于 tag/theme 这类“空 = 不筛选”而不是“空 = 全选”的控件。
 */
export const normalizeStringArrayByItemOptionsKeepEmpty = (item: any, value: unknown): string[] => {
  return normalizeStringArrayByOptionValues(value, getOptionValuesByItem(item), 'empty');
};

/**
 * 与 normalizeStringArrayByItemOptionsKeepEmpty 相同，
 * 但只接受叶子节点 value，避免层级型 options 把父节点 value 误判为合法值。
 */
export const normalizeLeafStringArrayByItemOptionsKeepEmpty = (item: any, value: unknown): string[] => {
  return normalizeStringArrayByOptionValues(value, getLeafOptionValuesByItem(item), 'empty');
};

export interface IStringArrayApiValueResult {
  value: string[];
  isAllSelected: boolean;
}

export const getStringArrayApiValueByItem = (item: any, value: unknown): IStringArrayApiValueResult => {
  const values = sanitizeValues(getStringArrayValue(value));
  const itemOptionValues = collectLeafOptionValues(item?.controlProps?.options);
  const optionValues = Array.from(
    new Set(isEmpty(itemOptionValues) ? getDefaultOptionValuesByItem(item) : itemOptionValues),
  );

  if (isEmpty(values) || isEmpty(optionValues)) {
    return {
      value: values,
      isAllSelected: false,
    };
  }

  const valueSet = new Set(values);
  const isAllSelected = optionValues.every((optionValue) => valueSet.has(optionValue));

  return {
    value: isAllSelected ? EMPTY_ARRAY : values,
    isAllSelected,
  };
};

export const getNumberRangeValue = (value: unknown): INumberRangeValue => {
  const rangeValue = value as INumberRangeValue | undefined;

  return {
    min: rangeValue?.min,
    max: rangeValue?.max,
  };
};

export const getComparableValue = (value: unknown): IComparableValue => {
  const comparableValue = value as IComparableValue | undefined;

  return {
    opt: comparableValue?.opt || '',
    value: comparableValue?.value ?? '',
  };
};

export const getFiniteNumberValue = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== 'string' || isEmpty(value)) {
    return null;
  }

  const normalizedValue = value.trim();

  if (!FINITE_NUMBER_TEXT_PATTERN.test(normalizedValue)) {
    return null;
  }

  const parsedValue = Number(normalizedValue);

  return Number.isFinite(parsedValue) ? parsedValue : null;
};

export const buildFullValueApiParams = (
  item: IFilterItemConfig,
  filterValue?: string[],
  params?: unknown,
): Record<string, string[]> | null => {
  if (isEmpty(filterValue)) {
    return null;
  }

  const unique = sanitizeValues(filterValue);

  if (isEmpty(unique)) {
    return null;
  }

  const field = resolveApiField(item, undefined, params);

  if (isEmpty(field)) {
    return null;
  }

  return {
    [field]: unique,
  };
};

export const buildStringArrayApiParamsByItem = (
  item: IFilterItemConfig,
  filterValue?: string[],
  params?: unknown,
): Record<string, string[]> | null => {
  if (isEmpty(filterValue)) {
    return null;
  }

  const { value, isAllSelected } = getStringArrayApiValueByItem(item, filterValue);

  if (isEmpty(value) && !isAllSelected) {
    return null;
  }

  const field = resolveApiField(item, undefined, params);

  if (isEmpty(field)) {
    return null;
  }

  return {
    [field]: value,
  };
};
